/**
 * Commitments (Vereinbarungen) API hooks.
 *
 * weship-v2 is the master: Flow pulls commitments in and the merchant
 * approves/declines them. The /commitments/* paths and the Commitment schema
 * only exist in src/generated/v1 AFTER the backend is restarted with
 * df_commitment + df_clientportal_api's new api/commitment routes and
 * `yarn generate:api-code` has been re-run. Until then tsc reports the paths
 * as unknown — the expected codegen gate (never commit FE hooks on generated
 * types without running codegen).
 */
import { components } from 'src/generated/v1';
import { useQueryClient } from '@tanstack/react-query';
import { $api } from './api';

export type Commitment = components['schemas']['Commitment'];
export type CommitmentDecline = components['schemas']['CommitmentDecline'];

export type CommitmentStatus = 'pending_customer' | 'active' | 'rejected_customer' | 'terminated' | 'superseded';

export const useCommitmentsQuery = () => {
  return $api.useQuery('get', '/commitments');
};

export const useCommitmentQuery = (id: number, options?: { enabled?: boolean }) => {
  return $api.useQuery(
    'get',
    '/commitments/{commitment_id}',
    {
      params: { path: { commitment_id: id } },
    },
    { ...options },
  );
};

export function invalidateCommitmentQueries(queryClient: ReturnType<typeof useQueryClient>) {
  const keys = [
    ['get', '/commitments'],
    ['get', '/commitments/{commitment_id}'],
  ];
  keys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: key });
  });
}

export const useCommitmentMutations = () => {
  const queryClient = useQueryClient();

  const approveMutation = $api.useMutation('post', '/commitments/{commitment_id}/approve', {
    onSuccess() {
      invalidateCommitmentQueries(queryClient);
    },
  });

  const declineMutation = $api.useMutation('post', '/commitments/{commitment_id}/decline', {
    onSuccess() {
      invalidateCommitmentQueries(queryClient);
    },
  });

  return { approveMutation, declineMutation };
};

export const COMMITMENT_STATUS_LABEL_KEY: Record<CommitmentStatus, string> = {
  pending_customer: 'commitments.status_pending_customer',
  active: 'commitments.status_active',
  rejected_customer: 'commitments.status_rejected_customer',
  terminated: 'commitments.status_terminated',
  superseded: 'commitments.status_superseded',
};
